import { throwError as observableThrowError, Observable, of, BehaviorSubject, TimeoutError } from 'rxjs';
import { map, retryWhen, concat, delay, take, switchMap, retry, catchError } from 'rxjs/operators';
import { Injectable, Injector } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpRequest,
  HttpHeaders,
} from '@angular/common/http';
import { LoggerService } from './logger.service';
import { SignApiReqValues, SyncCryptService } from './crypt/sync-crypt.service';
import { ERROR_CODES, BlendEvent, ErrCode, ErrorMessages } from '../shared/models';
import { BaseApiOutput } from '../shared/models/api';
import { UrlService } from './url.service';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { ITransferItemDownload } from 'src/sync';
import { BlendService } from '../shared/services/blend.service';
import { RegionService } from './region.service';
import { AuthQueryParams } from '../auth/shared/enums';
import { DOWNLOAD_CHUNK_TIMEOUT_MS } from '../shared/shared.constants';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private errorSubject = new BehaviorSubject<ErrCode | null>(null);
  public error$ = this.errorSubject.asObservable();

  constructor(
    private crypt: SyncCryptService,
    private http: HttpClient,
    private injector: Injector,
    private log: LoggerService,
    private url: UrlService,
    private router: Router,
    private regionService: RegionService,
  ) {}

  // Using injector.get instead of constructor injection to avoid circular dependency
  // TODO https://syncdev.atlassian.net/browse/UX-2531 Refactor to remove injector.get()
  private get blendService(): BlendService {
    return this.injector.get(BlendService);
  }

  public exec<T extends BaseApiOutput>(
    name: string,
    params: any,
    retryEnabled = true,
    customHeaders?: { [key: string]: string },
    queryParams = '',
    passthroughCodes: number[] = [],
  ): Observable<T> {
    const url = this.url.mkApi(environment.apihosts[0], name, queryParams);
    const headers = new HttpHeaders(customHeaders);
    return this.http.post<T>(url, params, { headers }).pipe(
      map((result) => {
        if (result.success === 0) {
          // Caller explicitly requested this error_code be returned as data rather than thrown.
          // WARNING: passthrough bypasses all side-effects in the following case statement.
          if (passthroughCodes.includes(result.error_code)) {
            return result;
          }
          this.log.error(`200 ${url} error occurred`);
          // TODO https://syncdev.atlassian.net/browse/COM-6658
          switch (result.error_code) {
            case 8050:
            case 8051:
              // suspended
              this.router.navigate(['/account-suspended'], {
                queryParams: { ...result.data, [AuthQueryParams.EMAIL]: null },
                queryParamsHandling: 'merge',
              });
              break;
            case 8054:
              // incomplete
              this.router.navigate(['/account-suspended'], {
                queryParams: { ...result.data, m: 2, [AuthQueryParams.EMAIL]: null },
                queryParamsHandling: 'merge',
              });
              break;
            case 8055:
              // migrating
              this.router.navigate(['/account-suspended'], {
                queryParams: { ...result.data, m: 1, [AuthQueryParams.EMAIL]: null },
                queryParamsHandling: 'merge',
              });
              break;
            case 8057:
              this.router.navigate(['/account-suspended'], { queryParams: { ...result.data, m: 3 } });
              break;
            case 8052:
              // accounting hold
              this.router.navigate(['/accounting-hold'], {
                queryParams: { ...result.data, [AuthQueryParams.EMAIL]: null },
                queryParamsHandling: 'merge',
              });
              break;

            case 8053:
              // accounting hold
              this.router.navigate(['/account-closed'], {
                queryParams: { [AuthQueryParams.EMAIL]: null },
                queryParamsHandling: 'merge',
                state: { reactivation_token: result.data?.reactivation_token },
              });
              break;
            default:
              this.log.error('Received an error code from the server ' + result.error_code);
              break;
          }
          throw ErrCode.fromApiLegacy(result);
        } else if (result.success === 1 && result.logout === 1 && name != 'sessiondelete') {
          this.log.error(`Received a logout from API on ${url}`);
          this.handleLogout();
          return;
        }
        this.log.info(`200 ${url}  successful`);
        return result;
      }),
      retryWhen((errors) => {
        return errors.pipe(
          delay(1000),
          take(3),
          switchMap((httpErr) => {
            if (httpErr instanceof HttpErrorResponse) {
              if (httpErr.status === 0) {
                // No internet connection
                this.log.error('No internet connection detected.');
                return observableThrowError(new ErrCode(ERROR_CODES.NO_INTERNET, ErrCode[9999]));
              }

              if (retryEnabled) {
                let shouldRetry = of(httpErr);
                this.log.error(`ERROR INPUT: ${JSON.stringify(params)}`);
                this.log.error(`ERROR OUTPUT: ${httpErr.status} ${httpErr.statusText} :: ${httpErr.url}`);
                switch (httpErr.status) {
                  case 401:
                    shouldRetry = observableThrowError(httpErr);
                    break;
                  case 400:
                  case 429:
                  case 500:
                    // do not retry, this is an error
                    shouldRetry = observableThrowError(httpErr);
                    break;
                  case 501:
                  case 502:
                  case 503:
                  case 504:
                    // this may be a temporary error
                    break;
                  default:
                    // default is to retry
                    shouldRetry = observableThrowError(httpErr);
                    break;
                }
                return shouldRetry;
              }
            }
            // httpErr is an API result
            return observableThrowError(httpErr);
          }),
          concat(observableThrowError(new ErrCode(ERROR_CODES.API_TALK_ERROR))),
        );
      }),
    );
  }
  /**
   * This sends an unsigned request ot the API
   * @param name the API name
   * @param params the API input parameters
   */
  public async send<T extends BaseApiOutput>(name: string, params: any): Promise<T> {
    try {
      const response = await this.exec<T>(name, params).toPromise();
      if (!response) {
        throw new ErrCode(ERROR_CODES.API_TALK_ERROR);
      }
      this.clearErrorOnSuccess();
      return <T>response;
    } catch (err) {
      this.handleError(name, params, err);
    }
  }

  /**
   * This builds the HTTP request to the EXTAPI.
   * Retry is enabled always. Regardless of the error produced from the ExtApi
   */
  private execExt<T extends BaseApiOutput>(path: string, method: string, params?: any): Observable<T> {
    const retryEnabled = true;
    const url = this.url.mkExtApiUrl(path);
    if (method === 'GET') {
      // Note: params for GET are query params
      return this.http.get<T>(url, { params: params }).pipe(
        map((result) => {
          if (result.success === 0) {
            this.log.error(`EXTAPI 200 ${url} error occurred`);
            this.log.error('EXTAPI Received an error code from the server ' + result.error_code);
            throw ErrCode.fromApiLegacy(result);
          }
          this.log.info(`200 ${url}  successful`);
          return result;
        }),
        retryWhen((errors) => {
          return errors.pipe(
            delay(1000),
            take(3),
            switchMap((httpErr) => {
              if (httpErr instanceof HttpErrorResponse && retryEnabled === true) {
                let shouldRetry = of(httpErr);
                this.log.error(`EXTAPI ERROR INPUT: ${JSON.stringify(params)}`);
                this.log.error(`EXTAPI ERROR OUTPUT: ${httpErr.status} ${httpErr.statusText} :: ${httpErr.url}`);

                if (httpErr.status > 300 && httpErr.status < 500) {
                  // Throw on 3xx and 4xx
                  shouldRetry = observableThrowError(httpErr);
                }
                return shouldRetry;
              } else {
                // httpErr is an API result
                return observableThrowError(httpErr);
              }
            }),
            concat(observableThrowError(new ErrCode(ERROR_CODES.API_TALK_ERROR))),
          );
        }),
      );
    } else if (method === 'POST') {
      // Note: params for POST are the body
      return this.http.post<T>(url, params).pipe(
        map((result) => {
          if (result.success === 0) {
            this.log.error(`EXTAPI 200 ${url} error occurred`);
            this.log.error('EXTAPI Received an error code from the server ' + result.error_code);
            throw ErrCode.fromApiLegacy(result);
          }
          this.log.info(`200 ${url}  successful`);
          return result;
        }),
        retryWhen((errors) => {
          return errors.pipe(
            delay(1000),
            take(3),
            switchMap((httpErr) => {
              if (httpErr instanceof HttpErrorResponse && retryEnabled === true) {
                let shouldRetry = of(httpErr);
                this.log.error(`EXTAPI ERROR INPUT: ${JSON.stringify(params)}`);
                this.log.error(`EXTAPI ERROR OUTPUT: ${httpErr.status} ${httpErr.statusText} :: ${httpErr.url}`);

                if (httpErr.status > 300 && httpErr.status < 500) {
                  // Throw on 3xx and 4xx
                  shouldRetry = observableThrowError(httpErr);
                }

                return shouldRetry;
              } else {
                // httpErr is an API result
                return observableThrowError(httpErr);
              }
            }),
            concat(observableThrowError(new ErrCode(ERROR_CODES.API_TALK_ERROR))),
          );
        }),
      );
    }
  }

  /**
   * This sends an unsigned request to the EXTERNAL API
   * @param name the API name
   * @param params the API input parameters
   */
  public async sendExt<T extends BaseApiOutput>(path: string, method: string, params?: any): Promise<T> {
    try {
      const response = await this.execExt<T>(path, method, params).toPromise();
      if (!response) {
        throw new ErrCode(ERROR_CODES.API_TALK_ERROR);
      }
      this.clearErrorOnSuccess();
      return <T>response;
    } catch (err) {
      this.handleError(path, params, err);
    }
  }

  // sends a signed request
  public async execute<T extends BaseApiOutput>(
    name: string,
    params: any,
    retry = true,
    signValues?: SignApiReqValues,
    queryParams = '',
    passthroughCodes: number[] = [],
  ): Promise<T> {
    try {
      const input = await this.crypt.signApiReq(params, signValues);
      const token = input.access_token;
      const headers: Record<string, string> = {};
      if (token) {
        headers['X-Session-Token'] = token;
      }
      const response = await this.exec<T>(name, input, retry, headers, queryParams, passthroughCodes).toPromise();
      if (!response) {
        throw new ErrCode(ERROR_CODES.API_TALK_ERROR);
      }
      this.clearErrorOnSuccess();
      return <T>response;
    } catch (err) {
      this.handleError(name, params, err);
    }
  }

  public async fetchText(url: string): Promise<string> {
    const response = await this.http.get(url, { observe: 'response', responseType: 'text' }).toPromise();
    return response.body;
  }
  // Resolve the downloadmulti host once (runs an isup check) so chunked fetches can reuse it.
  public mkDownloadMultiUrl(): Promise<string> {
    return this.url.mkDownloadMulti();
  }
  public async fetchThumbs(items: any, url?: string): Promise<ArrayBuffer> {
    const downloadUrl = url || (await this.url.mkDownloadMulti());
    const response = await this.http.post(downloadUrl, { items: items }, { responseType: 'arraybuffer' }).toPromise();
    return response;
  }
  public fetchFile(
    tItem: ITransferItemDownload,
    offset: number,
    reqLength: number,
    retry = 0,
  ): Observable<HttpEvent<unknown>> {
    const req = new HttpRequest('GET', this.url.mkDownloadUrl(tItem, offset, reqLength, retry), {
      reportProgress: true,
      responseType: 'arraybuffer',
      headers: new HttpHeaders({ timeout: String(DOWNLOAD_CHUNK_TIMEOUT_MS) }),
    });

    return this.http
      .request(req)
      .pipe(catchError((err) => observableThrowError(this.classifyDownloadError(err, tItem.cachekey))));
  }

  /**
   * Maps a chunk-fetch failure onto an ErrCode. Retry decisions belong to DownloadChunkFetchService —
   * this only assigns the code that both that retry loop and the transfer item's status depend on.
   */
  private classifyDownloadError(error: unknown, cachekey: string): unknown {
    if (error instanceof TimeoutError) {
      this.log.error('Chunk fetch timed out ' + cachekey);
      return new ErrCode(ERROR_CODES.DOWNLOAD_TIMEOUT);
    }

    if (!(error instanceof HttpErrorResponse)) {
      return error;
    }

    this.log.warn(`Received status ${error.status} ${error.statusText}`);
    switch (error.status) {
      case -1:
      case 0:
        this.log.error('Status -1, timeout ' + cachekey);
        return new ErrCode(ERROR_CODES.DOWNLOAD_CONNECTION_ERROR);
      case 404:
        this.log.error('404 error ' + cachekey);
        return new ErrCode(ERROR_CODES.FILE_UNAVAILABLE);
      case 500:
      case 501:
      case 502:
      case 503:
      case 504:
        return new ErrCode(ERROR_CODES.DOWNLOAD_CONNECTION_ERROR);
      case 510:
        this.log.error('Blobchunk failed for ' + cachekey);
        return new ErrCode(ERROR_CODES.FILE_CORRUPTED);
      default:
        this.log.error('Unknown error occurred ' + cachekey);
        // A rejected request fails identically on every host, so it must not burn the retry budget.
        if (error.status >= 400 && error.status < 500 && error.status !== 408 && error.status !== 429) {
          return new ErrCode(ERROR_CODES.DOWNLOAD_INVALID_RESPONSE);
        }
        return new ErrCode(ERROR_CODES.DOWNLOAD_CONNECTION_ERROR);
    }
  }

  public fetchFilePlain(tItem: ITransferItemDownload, retry = 0): Promise<ArrayBuffer> {
    const req = new HttpRequest('GET', tItem.renderFile.url, {
      reportProgress: false,
      responseType: 'arraybuffer',
    });

    return new Promise<ArrayBuffer>(async (resolve, reject) => {
      this.http
        .request<ArrayBuffer>(req)
        .pipe(
          retryWhen((errors) => {
            return errors.pipe(
              delay(1000),
              take(2),
              switchMap((httpErr) => {
                let shouldRetry = of(httpErr);
                if (httpErr instanceof HttpErrorResponse) {
                  this.log.warn(`Received status ${httpErr.status} ${httpErr.statusText}`);
                  switch (httpErr.status) {
                    case -1:
                    case 0:
                      // do not retry, this is an error
                      this.log.error('Status -1, timeout ' + tItem.cachekey);
                      break;
                    case 404:
                      this.log.error('404 error ' + tItem.cachekey);
                      shouldRetry = observableThrowError(new ErrCode(ERROR_CODES.FILE_UNAVAILABLE));
                      break;
                    case 500:
                    case 501:
                    case 502:
                    case 503:
                    case 504:
                      // this may be a temporary error
                      break;
                    case 510:
                      this.log.error('Blobchunk failed for ' + tItem.cachekey);
                      shouldRetry = observableThrowError(new ErrCode(ERROR_CODES.FILE_CORRUPTED));
                      break;
                    default:
                      // default is to retry
                      this.log.error('Unknown error occurred ' + tItem.cachekey);
                      shouldRetry = observableThrowError(new ErrCode(ERROR_CODES.DOWNLOAD_CONNECTION_ERROR));
                      break;
                  }
                  return shouldRetry;
                } else {
                  // httpErr is an API result
                  return observableThrowError(httpErr);
                }
              }),
              concat(observableThrowError(new ErrCode(ERROR_CODES.DOWNLOAD_CONNECTION_ERROR))),
            );
          }),
        )
        .subscribe(
          async (resp) => {
            if (resp.type === HttpEventType.Response) {
              const respBuffer = <ArrayBuffer>resp.body;
              resolve(respBuffer);
            } else {
              this.log.info('Recieved HTTP event : ' + resp);
            }
          },
          (err) => {
            this.log.e('Error subscribing to fetch file in download-worker', err);
            if (this.url.downloadhosts[retry + 1]) {
              this.log.warn(`Retrying download on host ${this.url.downloadhosts[retry + 1]}`);
              resolve(this.fetchFilePlain(tItem, retry + 1));
            } else {
              this.log.error(`Attempted ${retry} retries and failed, rejecting file`);
              reject(err);
            }
          },
        );
    });
  }

  public subscribe(): Observable<any> {
    const headers = new HttpHeaders({
      'Cache-Control': 'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
      Pragma: 'no-cache',
      Expires: '0',
      timeout: '600000',
    });
    return this.http.get(this.url.mkSubscribe(), { headers: headers }).pipe(retry(3));
  }

  public subscribeJob(jobId: string): Observable<any> {
    const headers = new HttpHeaders({
      'Cache-Control': 'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
      Pragma: 'no-cache',
      Expires: '0',
      timeout: '600000',
    });
    return this.http.get('/sub?cid=' + jobId, { headers: headers }).pipe(retry(99));
  }

  private handleLogout(): void {
    const returnTo = this.url.getReturnTo();
    this.router.navigate(['/logout'], { queryParams: { return_to: returnTo } });
  }

  public setError(err: ErrCode | null): void {
    this.errorSubject.next(err);
  }

  private clearErrorOnSuccess(): void {
    if (this.currentError?.code === ERROR_CODES.BOT_PROTECTION_BLOCKED_TRAFFIC) {
      this.setError(null);
    }
  }

  public get currentError(): ErrCode | null {
    return this.errorSubject.value;
  }

  private handleError(name: string, params: unknown, err: any): void {
    if (name === 'updateblend') {
      // updateblend runs async without await and gets called from stack, i.e. even user is not authenticated at some point
      // temporary ignore all updateblend errors (incl. 401) to prevent sending it to Sentry, we can get errors from BE
      // @todo do not send additional API requests (that require auth) from stack after we got 401 from at least one request
    } else if (err instanceof HttpErrorResponse) {
      if (err.status === 429) {
        throw new ErrCode(ERROR_CODES.TOO_MANY_SIGNIN_ATTEMPTS);
      } else if (err.status === 403 && err.error?.error_code === ERROR_CODES.BOT_PROTECTION_BLOCKED_TRAFFIC) {
        // Blocked Traffic case
        this.setError(new ErrCode(ERROR_CODES.BOT_PROTECTION_BLOCKED_TRAFFIC));
        this.blendService.track(BlendEvent.ERROR, {
          type: 'CloudFlare Error',
          detail: name,
        });
        throw new ErrCode(ERROR_CODES.BOT_PROTECTION_BLOCKED_TRAFFIC);
      } else if (err.status === 0 && !navigator.onLine) {
        // No internet connection
        throw new ErrCode(ERROR_CODES.NO_INTERNET, ErrorMessages[ERROR_CODES.NO_INTERNET]);
      } else if (err.status === 453) {
        // Unauthorized region
        this.regionService.redirectToRegion(err.error.region);
      } else if (err.error && err.error.error_code) {
        throw new ErrCode(err.error.error_code, err.error.error_msg);
      } else if (err.status) {
        throw new ErrCode(parseInt('9' + err.status, 10));
      } else {
        throw ErrCode.fromApiLegacy(err.error);
      }
    } else if (err.error_code != undefined && err.error_msg != undefined) {
      if (err.error_code == 0 && err.error_msg.indexOf('NO CONFIG DATABASE') > -1) {
        this.log.error('no config database ' + err.error_msg);
        throw new ErrCode(this.getCodeForShardErr(err.error_msg));
      } else {
        this.log.error(`API error ${name}`);
        this.log.error(`ERROR INPUT: ${JSON.stringify(params)}`);
        this.log.error(`ERROR OUTPUT ${JSON.stringify(err)}`);
        throw ErrCode.fromApiLegacy(err);
      }
    } else if (err instanceof ErrCode) {
      this.log.error(`API error ${name}`);
      this.log.error(`ERROR INPUT: ${JSON.stringify(params)}`);
      this.log.error(`ERROR OUTPUT ${JSON.stringify(err)}`);
      if (err.code === ERROR_CODES.ACCOUNT_PROCESSING_CHANGES || err.code === ERROR_CODES.SHARE_FILE_LIMIT) {
        throw new ErrCode(err.code);
      }
      throw err;
    } else if (err.success === 0) {
      this.log.error(`ERROR INPUT: ${JSON.stringify(params)}`);
      this.log.error(`ERROR OUTPUT ${JSON.stringify(err)}`);
      throw new ErrCode(ERROR_CODES.API_TALK_ERROR);
    } else {
      throw new ErrCode(ERROR_CODES.API_TALK_ERROR);
    }
  }

  private getCodeForShardErr(errMsg: string): number {
    let code = 1100;
    if (errMsg.indexOf('linkcachekeys_') > -1) {
      code = 1101;
    } else if (errMsg.indexOf('invitecachekeys_') > -1) {
      code = 1102;
    } else if (errMsg.indexOf('refcodes_') > -1) {
      code = 1103;
    } else if (errMsg.indexOf('verifyusers_') > -1) {
      code = 1104;
    }

    return code;
  }
}
